import React, { useContext, useMemo } from 'react';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import killswitch from '@atlassian/jira-killswitch/src/index.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { getAnalyticsWebClientPromise } from '@atlassian/jira-product-analytics-web-client-async';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import {
	PostOfficeProvider as PostOfficeBaseProvider,
	usePostOfficeRouteUpdate,
} from '@atlassian/post-office-context';
import { useRouter } from '@atlassian/react-resource-router';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import {
	subproductToEdition,
	tenantToPostOfficeContext,
	transformJiraEnvToPostOfficeEnvironment,
} from './tenant-to-post-office-context.tsx';
import { CrossFlowPostOfficeContext } from './cross-flow-post-office-provider.tsx';
import { getOnboardingParams } from './onboarding-params.tsx';

const JiraPostOfficeProvider = ({ children }: { children: React.ReactNode }) => {
	const { data } = useProject();
	const isSiteAdmin = useIsSiteAdmin();
	const isAdmin = useIsAdmin();
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const locale = useLocale();
	const environment = useEnvironment();
	const { data: orgId } = useOrgId();
	const editions = useAppEditions();
	const subproduct = data?.type;
	const edition = subproductToEdition(subproduct, editions);
	const additionalContext = useContext(CrossFlowPostOfficeContext);

	const context = useMemo(() => {
		return {
			current: {
				...tenantToPostOfficeContext({
					activationId,
					cloudId,
					isAdmin,
					isSiteAdmin,
					locale,
				}),
				orgId,
				productEdition: edition || undefined,
				subproduct,
				...(fg('jira_xflow_quick_actions') ? additionalContext : {}),
				...(fg('jira-onboarding-hub-gate') ? getOnboardingParams() : {}),
			},
		};
	}, [
		cloudId,
		orgId,
		activationId,
		subproduct,
		isSiteAdmin,
		isAdmin,
		locale,
		edition,
		additionalContext,
	]);

	const env = useMemo(
		() => ({ env: transformJiraEnvToPostOfficeEnvironment(environment) }),
		[environment],
	);

	const analyticsClient = useMemo(
		() => getAnalyticsWebClientPromise().then((client) => client.getInstance()),
		[],
	);

	return (
		<PostOfficeBaseProvider
			context={context}
			environment={env}
			analyticsClientInstance={analyticsClient}
		>
			{children}
		</PostOfficeBaseProvider>
	);
};

export const PostOfficeProvider = ({ children }: { children: React.ReactNode }) => {
	// Using the Feature Gate used for the placement app-switcher-discovery-section because PostOfficeProvider is always needed when the app-switcher-discovery-section is enabled
	const isPostOfficeEnabled = !killswitch('killswitch_post_office_in_jira');

	return isPostOfficeEnabled ? (
		<ErrorBoundary id="postOfficeJiraProvider" render={() => <>{children}</>}>
			<JiraPostOfficeProvider>{children}</JiraPostOfficeProvider>
		</ErrorBoundary>
	) : (
		<>{children}</>
	);
};

const ReactResourceRouterListener = ({ children }: { children: React.ReactNode }) => {
	const [route] = useRouter();

	usePostOfficeRouteUpdate([route.basePath, route.location.pathname]);

	return <>{children}</>;
};

export const PostOfficeRouterListener = ({ children }: { children: React.ReactNode }) => {
	const isPostOfficeEnabled = !killswitch('killswitch_post_office_in_jira');

	return isPostOfficeEnabled && fg('post-office-screen-space-overlay-jfe') ? (
		<ReactResourceRouterListener>{children}</ReactResourceRouterListener>
	) : (
		<>{children}</>
	);
};
